import React, { useCallback, useEffect, useRef, useState} from 'react';
import { TextField, ScrollBar } from '@framework-controls/core';
import { strings } from 'src/localization';
import { IView } from 'Framework';
import { Customer, ApptCust, UpdateApptCustInp, ascCtaInteractionInput, ascFormEngagementInput, ascPageviewInput, ascFormSubmissionInput, ascFormSubmissionSvcInput, ascElementConfigurationInput } from 'src/models';
import Footer from '../Footer/Footer';
import { ComponentMapIds } from '../map';
import css from './OEMCustSearch.module.scss';
import { ascCtaInteraction, ascFormEngagement, ascFormSubmission, ascFormSubmissionSvc, ascPageview,
    ascElementConfiguration } from 'src/helpers/EventHelper/EventHelper';

export interface IProps {
    custInfo?: Customer;
    specProfile: string;
    store: string;
    branch: string;
    apptCust: ApptCust;
    errMsg?: string;
    loading: 'idle' | 'pending' | 'succeeded' | 'failed';
    myGMRewardsLaunched: string | undefined;
    mainPaneViewOrder: IView[];
    theme: string;
    pushToMainPanes: (inputView: IView) => void;
    pushToLeftPane: (inputView: IView) => void;
    spmCustomerCentral: (cust: Customer) => void;
    onAppCustUpdate: (apptCust: UpdateApptCustInp) => void;
    onCustUpdate: (cust: Customer) => void;
    setLoadingStateToIdle: () => void;
    clearError: () => void;
    gmMemberSearch: (inp: string) => void;
}

const emailRegex = new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))/.source +
    /@/.source + /((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.source);

const phoneRegex = new RegExp(/^(\d{3})[\s.-]?(\d{3})[\s.-]?(\d{4})$/);

const alphaNumeric = new RegExp(/[a-zA-Z0-9]/);

function formatValue(value) {
    let formattedValue = value;

    const phoneMatch = formattedValue.match(phoneRegex);

    if (phoneMatch) {
        formattedValue = `(${phoneMatch[1]}) ${phoneMatch[2]}-${phoneMatch[3]}`;
    }

    return formattedValue;
}

const OEMCustSearch = (props: IProps) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneEmail, setPhoneEmail] = useState(props.apptCust.custEmailAddress === '' ?
        props.apptCust.custPhoneNumber : props.apptCust.custEmailAddress);
    const [phoneEmailError, setPhoneEmailError] = useState(false);
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const initialValue = useRef(formatValue(phoneEmail));
    const [firstEvent, setFirstEvent] = useState(false);

    function firstNameHandler(_: string, value: string) {
        setFirstName(value);
    }

    function lastNameHandler(_: string, value: string) {
        setLastName(value);
    }

    const emailPhoneChangeHandler = (_: string, value: string) => {
        let formattedValue = '';

        if (phoneEmail === '') {
            const validFirstCharInput = value[0]?.match(alphaNumeric);

            if (!validFirstCharInput) {
                return;
            }
        }

        if (phoneEmail.match(phoneRegex)) {
            formattedValue = value.replace(/[^0-9]/g, '');

            if (formattedValue.length === 11 && formattedValue[0] === '1') {
                formattedValue = formattedValue.slice(1);
            }

            setPhoneEmail(formattedValue);
        } else {
            setPhoneEmail(value);
        }
    };

    const skipHandler = (e: any) => {
        setFirstName('');
        setLastName('');

        const apptCustInp: UpdateApptCustInp = {
            nameId: '',
            firstName: '',
            lastName: '',
            emailAddress: phoneEmail.match(emailRegex) ? phoneEmail.trim() : '',
            phoneNumber: phoneEmail.match(phoneRegex) ? phoneEmail.trim() : '',
            phoneType: '',
            prefContact: '',
            loggedIn: false,
        };
        props.onAppCustUpdate(apptCustInp);

        if (apptCustInp.emailAddress && props.myGMRewardsLaunched === 'Y') {
            props.gmMemberSearch(apptCustInp.emailAddress);
        }
        props.pushToMainPanes({
            viewId: ComponentMapIds.AddVehicle,
            component: ComponentMapIds.AddVehicle,
        });
        props.pushToLeftPane({
            viewId: ComponentMapIds.PropertyOptions,
            component: ComponentMapIds.PropertyOptions,
        });
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Skip,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.Skip,
            elementType: 'footer',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
    };

    const searchCustomerHandler = (e: any) => {
        let formattedPhoneNumber = '';
        if (phoneEmail.match(phoneRegex)) {
            formattedPhoneNumber = phoneEmail.replace(/[^0-9]/g, '');
        }

        const custInp: Customer = {
            nameId: '',
            firstName: firstName,
            lastName: lastName,
            emailAddress: phoneEmail.match(emailRegex) ? phoneEmail.trim() : '',
            phoneNumber: formattedPhoneNumber,
            login: false,
            prefContact: '',
            textOptIn: '',
            emailOptIn: '',
            streetAddress: '',
            stateAddress: '',
            city: '',
            zipCode: '',
            addVehNameId: '',
            gmRedeemAmt: '',
            encryptFirst: '',
            encryptLast: '',
            encryptEmail: '',
            encryptPhone: '',
            ascCustId: '',
        };

        if (custInp.emailAddress && props.myGMRewardsLaunched === 'Y') {
            props.gmMemberSearch(custInp.emailAddress);
        }
        props.onAppCustUpdate({ ...custInp, emailAddress: '', phoneNumber: '' });
        props.spmCustomerCentral(custInp);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: strings.Proceed,
            elementType: 'footer',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, e);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'contact',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.Proceed,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp);
        const ascFormSubmissionInp: ascFormSubmissionInput = {
            pageType: 'service',
            commType: 'form',
            commOutcome: 'engage',
            submissionId: props.custInfo?.ascCustId! ??'',
            elementText: strings.Proceed,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'consumer_contact',
        };
        ascFormSubmission(ascFormSubmissionInp);
        const ascFormSubmissionSvcInp : ascFormSubmissionSvcInput = {
            pageType: 'service',
            commType: 'form',
            commOutcome: 'engage',
            submissionId: props.custInfo?.ascCustId! ?? '',
            elementText: strings.Proceed,
            department: 'service',
            formName: 'TMNA_Cust_Search',
            formType: 'service_appointment',
        };
        ascFormSubmissionSvc(ascFormSubmissionSvcInp);
    };
    const firstBlurHandler = useCallback(() => {
        setFirstNameError(firstName.length > 30);
        if (firstEvent) {
            const ascFormEngagementInp: ascFormEngagementInput = {
                pageType: 'service',
                commType: 'form',
                commStatus: 'engage',
                elementText: strings.FirstName,
                department: 'service',
                formName: 'Service_Portal_2',
                formType: 'service_appointment',
            };
            ascFormEngagement(ascFormEngagementInp);
        }
    }, [firstName.length, firstEvent]);

    const lastBlurHandler = useCallback(() => {
        setLastNameError(lastName.length > 30);
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.LastName,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp);
    }, [lastName.length]);

    const emailPhoneBlurHandler = useCallback(() => {
        setPhoneEmailError(!(phoneEmail.match(emailRegex) || phoneEmail.match(phoneRegex)));
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.EmailOrPhone,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp);
    }, [phoneEmail]);

    useEffect(() => {
        if (props.loading === 'succeeded') {
            if (props.errMsg === '1008') {
                props.pushToMainPanes({
                    viewId: ComponentMapIds.AddVehicle,
                    component: ComponentMapIds.AddVehicle,
                });
                props.pushToLeftPane({
                    viewId: ComponentMapIds.PropertyOptions,
                    component: ComponentMapIds.PropertyOptions,
                });
            } else {
                props.pushToMainPanes({
                    viewId: ComponentMapIds.VehSel,
                    component: ComponentMapIds.VehSel,
                });
            }

            props.setLoadingStateToIdle();
            props.clearError();
            setFirstName('');
            setLastName('');
        }
    }, [props.loading, props.errMsg, props.pushToMainPanes, props.pushToLeftPane, props.setLoadingStateToIdle, props.clearError]);

    useEffect(() => {
        if (props.mainPaneViewOrder){
            if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1]?.viewId === ComponentMapIds.OEMCustSearch) {
                const ascPageViewInp: ascPageviewInput = {
                    pageType: 'service',
                    errorCode: '',
                    itemResults: '',
                };
                ascPageview(ascPageViewInp);
            }
        }
    }, [props.mainPaneViewOrder]);

    let isDisabled = true;
    if (firstName !== '' && firstName.length <= 30 && lastName !== '' && lastName.length <= 30 &&
        (phoneEmail.match(phoneRegex) || phoneEmail.match(emailRegex))) {
        isDisabled = false;
    }
    const firstEventHandler = () => {
        setFirstEvent(true);
    };


    return (
        <>
            <ScrollBar scrollVertical>
                <div className={css.body} onClick={firstEventHandler} onKeyPress={firstEventHandler}>
                    <h2 className={
                        props.theme === 'mazda' ? css.searchMazda :
                            props.theme === 'genesis' ? css.searchGenesis :
                                undefined
                    }>{strings.KeepSearching}</h2>
                    <h3>{strings.formatString(strings.NationwideVehicleSearch, initialValue.current).toString()}</h3>
                    <hr />
                    <span>{strings.RequiredFieldsToSearch}</span>
                    <div className={css.inputContainer}>
                        <div className={css.input}>
                            <TextField
                                id='firstName'
                                label={strings.FirstName}
                                value={firstName}
                                size='full'
                                onChange={firstNameHandler}
                                onBlur={firstBlurHandler}
                                type='text'
                                autoFocus
                                error={firstNameError}
                                helperText={firstNameError ? strings.FirstNameLengthError : ''}
                            />
                        </div>
                        <div className={css.input}>
                            <TextField
                                id='lastName'
                                label={strings.LastName}
                                value={lastName}
                                size='full'
                                onChange={lastNameHandler}
                                onBlur={lastBlurHandler}
                                type='text'
                                error={lastNameError}
                                helperText={lastNameError ? strings.LastNameLengthError : ''}
                            />
                        </div>
                        <div className={css.input}>
                            <TextField
                                id='phoneEmail'
                                label={strings.EmailOrPhone}
                                value={formatValue(phoneEmail)}
                                size='full'
                                onChange={emailPhoneChangeHandler}
                                onBlur={emailPhoneBlurHandler}
                                error={phoneEmailError}
                                helperText={phoneEmailError ? strings.ValidEmailPhone : ''}
                                type='text'
                            />
                        </div>
                    </div>
                </div>
            </ScrollBar>

            <Footer
                id='OEMCustFooter'
                handler={(searchCustomerHandler)}
                isDisabled={isDisabled}
                message={strings.Proceed}
                secondaryMessage={strings.Skip}
                secondaryhandler={skipHandler}
                theme={props.theme}/>
        </>
    );
};

export default OEMCustSearch;